/**
 * Deterministic Fix Priority ranking for Information Systems list.
 *
 * Separation of concerns:
 * - vulnerability severity / confirmed risks → base Fix Priority band
 * - business criticality, exposure, age, counts → context sort within band only
 * - Security Health (TSH) → separate (unchanged Home formula helpers below)
 * - official Risk Engine → not used / not replaced
 *
 * Missing / not-assessed compliance is never a confirmed violation.
 * Context points alone NEVER escalate MEDIUM → CRITICAL.
 */

const BAND_RANK = Object.freeze({ NONE: 0, LOW: 1, MEDIUM: 2, HIGH: 3, CRITICAL: 4 })
const CRIT_CONTEXT = Object.freeze({ CRITICAL: 40, HIGH: 28, MEDIUM: 14, LOW: 6, INFO: 0 })

function n(v) {
  const x = Number(v)
  return Number.isFinite(x) ? x : 0
}

function upper(v) {
  return String(v || '').trim().toUpperCase()
}

function daysSince(iso) {
  if (!iso) return null
  const t = new Date(iso).getTime()
  if (!Number.isFinite(t)) return null
  return Math.max(0, Math.floor((Date.now() - t) / 86400000))
}

function bandRank(band) {
  return BAND_RANK[upper(band)] || 0
}

function maxBand(a, b) {
  return bandRank(a) >= bandRank(b) ? upper(a || 'NONE') : upper(b || 'NONE')
}

function severityToBand(sev) {
  const s = upper(sev)
  if (s === 'CRITICAL') return 'CRITICAL'
  if (s === 'HIGH') return 'HIGH'
  if (s === 'MEDIUM') return 'MEDIUM'
  if (s === 'LOW') return 'LOW'
  if (s === 'INFO') return 'LOW'
  return 'NONE'
}

function isConfirmedComplianceGap(scopeEntry) {
  if (!scopeEntry || typeof scopeEntry !== 'object') return false
  if (scopeEntry.available === false) return false
  const verified = scopeEntry.verifiedPercent
  const self = scopeEntry.selfAssessedPercent
  if (verified == null && self == null) return false
  const status = upper(scopeEntry.verifiedStatus)
  if (status === 'NOT_STARTED' || status === 'NOT_ASSESSED' || status === 'UNKNOWN') return false
  if (verified != null && Number.isFinite(Number(verified)) && Number(verified) < 40) return true
  if (verified == null && self != null && Number.isFinite(Number(self)) && Number(self) < 40) {
    return true
  }
  return false
}

function isInternetExposed(row = {}) {
  const env = String(row.environment || '').toLowerCase()
  return (
    Boolean(row.internetExposed) ||
    env.includes('prod') ||
    env.includes('dmz') ||
    env.includes('public') ||
    (row.topVulnerabilities || []).some((v) => v && v.internetExposed)
  )
}

/**
 * Confirmed impact scenario that may escalate above scanner severity label.
 * Requires explicit evidence — never inferred from business criticality alone.
 */
function confirmedEscalationFromVuln(v, rowInternetExposed) {
  if (!v || typeof v !== 'object') return null
  const sev = upper(v.severity)
  const exposed = Boolean(v.internetExposed) || rowInternetExposed
  const exploitable = v.isExploitable === true
  const impact = upper(v.confirmedImpactSeverity || v.impactSeverity || '')
  const source = v.id || v.cve || v.title || 'vulnerability'

  if (impact === 'CRITICAL' || impact === 'HIGH') {
    return {
      toBand: impact,
      reason: `Confirmed impact severity ${impact} on finding`,
      source: String(source),
      evidence: 'confirmedImpactSeverity',
    }
  }

  // Exploitable + exposed is a confirmed critical-path scenario (may raise MEDIUM→CRITICAL)
  if (exploitable && exposed && (sev === 'MEDIUM' || sev === 'HIGH' || sev === 'LOW')) {
    return {
      toBand: 'CRITICAL',
      reason: 'Exploitable finding on internet/production-exposed asset',
      source: String(source),
      evidence: 'isExploitable+internetExposed',
    }
  }

  // Exploitable without exposure: raise at most to HIGH (not CRITICAL)
  if (exploitable && !exposed && (sev === 'MEDIUM' || sev === 'LOW')) {
    return {
      toBand: 'HIGH',
      reason: 'Exploitable finding (not internet-exposed)',
      source: String(source),
      evidence: 'isExploitable',
    }
  }

  return null
}

function confirmedEscalationFromRisk(r) {
  if (!r || typeof r !== 'object') return null
  const sev = upper(r.severity)
  const residual = n(r.residualRisk)
  const impact = upper(r.confirmedImpactSeverity || r.impactSeverity || '')
  const source = r.id || r.sourceId || r.title || 'risk'

  if (sev === 'CRITICAL' || impact === 'CRITICAL') {
    return {
      toBand: 'CRITICAL',
      reason: sev === 'CRITICAL' ? 'Confirmed open risk severity CRITICAL' : 'Confirmed risk impact CRITICAL',
      source: String(source),
      evidence: sev === 'CRITICAL' ? 'risk.severity' : 'risk.confirmedImpactSeverity',
    }
  }

  // High residual on a MEDIUM risk can confirm HIGH impact — not CRITICAL by residual alone
  if ((sev === 'MEDIUM' || sev === 'LOW') && residual >= 12) {
    return {
      toBand: 'HIGH',
      reason: `Confirmed residual risk ${residual} on ${sev || 'unspecified'} risk`,
      source: String(source),
      evidence: 'residualRisk>=12',
    }
  }

  if (sev === 'HIGH' || impact === 'HIGH') {
    return {
      toBand: 'HIGH',
      reason: 'Confirmed open risk severity HIGH',
      source: String(source),
      evidence: 'risk.severity',
    }
  }

  return null
}

/**
 * @returns {{
 *   score: number,
 *   band: string,
 *   baseBand: string,
 *   contextScore: number,
 *   factors: Array<{ id: string, role: string, weight?: number, detail: string, source?: string }>,
 *   primaryFactor: object|null,
 *   contextFactors: Array<object>,
 *   escalation: object|null,
 *   problemCount: number,
 *   hasCriticalOrHigh: boolean,
 * }}
 */
export function computeRemediationPriority(row = {}, executive = null) {
  const factors = []
  const internetExposed = isInternetExposed(row)

  let baseBand = 'NONE'
  let primaryFactor = null
  let bestEscalation = null

  const considerPrimary = (candidate) => {
    if (!candidate) return
    if (!primaryFactor || bandRank(candidate.band) > bandRank(primaryFactor.band)) {
      primaryFactor = candidate
    } else if (
      bandRank(candidate.band) === bandRank(primaryFactor.band) &&
      n(candidate.weight) > n(primaryFactor.weight)
    ) {
      primaryFactor = candidate
    }
  }

  const considerEscalation = (esc) => {
    if (!esc || !esc.toBand) return
    if (!bestEscalation || bandRank(esc.toBand) > bandRank(bestEscalation.toBand)) {
      bestEscalation = esc
    }
  }

  // —— Confirmed vulnerability problems (base band) ——
  let critHighVulns = 0
  for (const v of row.topVulnerabilities || []) {
    const band = severityToBand(v.severity)
    if (band === 'NONE') continue
    if (band === 'CRITICAL' || band === 'HIGH') critHighVulns += 1
    baseBand = maxBand(baseBand, band)
    const factor = {
      id: 'VULN_SEVERITY',
      role: 'primary',
      band,
      weight: bandRank(band) * 10,
      detail: `${upper(v.severity)} vulnerability: ${v.cve || v.title || v.id || 'finding'}`,
      source: String(v.id || v.cve || v.title || 'vulnerability'),
    }
    factors.push(factor)
    considerPrimary(factor)
    considerEscalation(confirmedEscalationFromVuln(v, internetExposed))
  }

  // Severity distribution may show C/H not present in truncated tops
  const dist = row.severityDistribution || row.severity || null
  if (dist && typeof dist === 'object') {
    if (n(dist.critical ?? dist.CRITICAL) > 0) {
      baseBand = maxBand(baseBand, 'CRITICAL')
      const factor = {
        id: 'VULN_SEVERITY_DIST',
        role: 'primary',
        band: 'CRITICAL',
        weight: 40,
        detail: `${n(dist.critical ?? dist.CRITICAL)} CRITICAL finding(s) in severity distribution`,
        source: 'severityDistribution',
      }
      factors.push(factor)
      considerPrimary(factor)
      critHighVulns += n(dist.critical ?? dist.CRITICAL)
    } else if (n(dist.high ?? dist.HIGH) > 0) {
      baseBand = maxBand(baseBand, 'HIGH')
      const factor = {
        id: 'VULN_SEVERITY_DIST',
        role: 'primary',
        band: 'HIGH',
        weight: 30,
        detail: `${n(dist.high ?? dist.HIGH)} HIGH finding(s) in severity distribution`,
        source: 'severityDistribution',
      }
      factors.push(factor)
      considerPrimary(factor)
      critHighVulns += n(dist.high ?? dist.HIGH)
    } else if (n(dist.medium ?? dist.MEDIUM) > 0) {
      baseBand = maxBand(baseBand, 'MEDIUM')
      const factor = {
        id: 'VULN_SEVERITY_DIST',
        role: 'primary',
        band: 'MEDIUM',
        weight: 20,
        detail: `${n(dist.medium ?? dist.MEDIUM)} MEDIUM finding(s) in severity distribution`,
        source: 'severityDistribution',
      }
      factors.push(factor)
      considerPrimary(factor)
    } else if (n(dist.low ?? dist.LOW) > 0) {
      baseBand = maxBand(baseBand, 'LOW')
      const factor = {
        id: 'VULN_SEVERITY_DIST',
        role: 'primary',
        band: 'LOW',
        weight: 10,
        detail: `${n(dist.low ?? dist.LOW)} LOW finding(s) in severity distribution`,
        source: 'severityDistribution',
      }
      factors.push(factor)
      considerPrimary(factor)
    }
  }

  // —— Confirmed open risks ——
  for (const r of row.topOpenRisks || []) {
    const band = severityToBand(r.severity)
    if (band !== 'NONE') {
      baseBand = maxBand(baseBand, band)
      const factor = {
        id: 'CONFIRMED_RISK',
        role: 'primary',
        band,
        weight: bandRank(band) * 10 + Math.min(15, n(r.residualRisk)),
        detail: `${upper(r.severity) || 'OPEN'} risk: ${r.title || r.id || 'risk'}`,
        source: String(r.id || r.sourceId || r.title || 'risk'),
      }
      factors.push(factor)
      considerPrimary(factor)
    }
    considerEscalation(confirmedEscalationFromRisk(r))
  }

  // Failed controls / blockers / assessed compliance gaps → at most MEDIUM base (not CRITICAL)
  const failed = n(row.failedControlsCount) || (row.topFailedControls || []).length
  if (failed > 0) {
    baseBand = maxBand(baseBand, 'MEDIUM')
    const factor = {
      id: 'FAILED_CONTROLS',
      role: 'primary',
      band: 'MEDIUM',
      weight: Math.min(25, 8 + failed * 3),
      detail: `${failed} failed / incomplete control(s)`,
      source: 'failedControls',
    }
    factors.push(factor)
    considerPrimary(factor)
  }

  const blockers = n(executive?.blockerCount ?? row.blockerCount)
  if (blockers > 0) {
    baseBand = maxBand(baseBand, 'MEDIUM')
    const factor = {
      id: 'BLOCKERS',
      role: 'primary',
      band: 'MEDIUM',
      weight: Math.min(20, blockers * 5),
      detail: `${blockers} readiness blocker(s)`,
      source: 'blockers',
    }
    factors.push(factor)
    considerPrimary(factor)
  }

  const scopes = [...(row.complianceScope || []), ...(executive?.complianceScope || [])]
  let confirmedGaps = 0
  for (const s of scopes) {
    if (isConfirmedComplianceGap(s)) confirmedGaps += 1
  }
  if (confirmedGaps > 0) {
    baseBand = maxBand(baseBand, 'MEDIUM')
    const factor = {
      id: 'CONFIRMED_COMPLIANCE_GAPS',
      role: 'primary',
      band: 'MEDIUM',
      weight: Math.min(24, confirmedGaps * 8),
      detail: `${confirmedGaps} confirmed assessed compliance gap(s)`,
      source: 'complianceScope',
    }
    factors.push(factor)
    considerPrimary(factor)
  }

  // No confirmed problems → LOW (not CRITICAL). Do not invent critical risk.
  if (baseBand === 'NONE') baseBand = 'LOW'

  // —— Gated escalation (evidence required) ——
  let band = baseBand
  let escalation = null
  if (bestEscalation && bandRank(bestEscalation.toBand) > bandRank(baseBand)) {
    band = upper(bestEscalation.toBand)
    escalation = {
      fromBand: baseBand,
      toBand: band,
      reason: bestEscalation.reason,
      source: bestEscalation.source,
      evidence: bestEscalation.evidence,
    }
    factors.push({
      id: 'ESCALATION',
      role: 'escalation',
      band,
      weight: bandRank(band) * 10,
      detail: `Raised ${baseBand} → ${band}: ${bestEscalation.reason}`,
      source: bestEscalation.source,
    })
  }

  // —— Context factors (sort within band ONLY — never raise band) ——
  let contextScore = 0
  const contextFactors = []

  const pushContext = (id, weight, detail) => {
    if (!weight) return
    contextScore += weight
    const f = { id, role: 'context', weight, detail }
    factors.push(f)
    contextFactors.push(f)
  }

  const crit = upper(row.criticality)
  pushContext('SYSTEM_CRITICALITY', CRIT_CONTEXT[crit] || 0, `Business criticality ${crit || 'unset'}`)

  if (internetExposed) {
    pushContext('INTERNET_EXPOSURE', 18, 'Internet / production exposure')
  }

  const sensitiveCats = new Set([
    'PERSONAL DATA',
    'SPECIAL CATEGORY PERSONAL DATA',
    'PAYMENT DATA',
    'BANKING SECRECY',
    'FINANCIAL DATA',
  ])
  const dataCats = row.criticalDataCategories || []
  const sensitiveCount = dataCats.filter((c) => sensitiveCats.has(upper(c))).length
  if (sensitiveCount > 0) {
    pushContext('CRITICAL_DATA', Math.min(16, 6 + sensitiveCount * 3), `${sensitiveCount} sensitive data categor(ies)`)
  }

  const vulnCount = n(row.vulnerabilityCount) || (row.topVulnerabilities || []).length
  if (vulnCount > 0) {
    pushContext('FINDING_VOLUME', Math.min(20, vulnCount), `${vulnCount} open finding(s)`)
  }

  let freshestAge = null
  for (const v of row.topVulnerabilities || []) {
    const age = daysSince(v.detectedAt || v.lastDetectedAt)
    if (age == null) continue
    if (freshestAge == null || age < freshestAge) freshestAge = age
  }
  if (freshestAge != null && freshestAge <= 14 && bandRank(band) >= bandRank('MEDIUM')) {
    pushContext('FRESH_FINDINGS', 8, `Newest relevant finding ~${freshestAge}d ago`)
  }
  if (freshestAge != null && freshestAge > 90 && bandRank(band) >= bandRank('HIGH')) {
    pushContext('STALE_OPEN_HIGH', 6, `Open high-severity finding age ~${freshestAge}d`)
  }

  const scanAge = daysSince(row.lastScan)
  if (scanAge != null && scanAge > 60 && bandRank(baseBand) >= bandRank('MEDIUM')) {
    pushContext('STALE_ASSESSMENT', Math.min(10, Math.floor(scanAge / 30) * 2), `Last scan ~${scanAge}d ago with open issues`)
  }

  const incomplete =
    !(row.ownerLabel || '').trim() ||
    !(row.applicableStandards || []).length ||
    n(row.linkedAssetsCount) === 0
  if (incomplete && (crit === 'CRITICAL' || crit === 'HIGH') && bandRank(baseBand) >= bandRank('MEDIUM')) {
    pushContext('INCOMPLETE_PASSPORT', 5, 'Incomplete passport on business-critical system with open issues')
  }

  // Sort key: band dominates; context only orders inside the same band
  const score = bandRank(band) * 1000 + Math.min(999, Math.round(contextScore))

  const problemCount =
    (n(row.vulnerabilityCount) || (row.topVulnerabilities || []).length) +
    (n(row.openRisksCount) || (row.topOpenRisks || []).length) +
    (n(row.failedControlsCount) || (row.topFailedControls || []).length) +
    blockers +
    confirmedGaps

  const hasCriticalOrHigh =
    band === 'CRITICAL' ||
    band === 'HIGH' ||
    critHighVulns > 0 ||
    (row.topOpenRisks || []).some((r) => ['CRITICAL', 'HIGH'].includes(upper(r.severity)))

  // Deduplicate primary-looking factors for display: keep one primary pointer
  if (primaryFactor && primaryFactor.role === 'primary') {
    // ensure listed
  } else if (baseBand !== 'LOW' || problemCount > 0) {
    primaryFactor = primaryFactor || {
      id: 'NO_CONFIRMED_SEVERE_PROBLEM',
      role: 'primary',
      band: baseBand,
      weight: 0,
      detail: 'No confirmed CRITICAL/HIGH problem — band from available findings only',
      source: 'none',
    }
  }

  return {
    score,
    band,
    baseBand,
    contextScore: Math.round(contextScore),
    factors,
    primaryFactor: primaryFactor
      ? {
          id: primaryFactor.id,
          detail: primaryFactor.detail,
          band: primaryFactor.band || baseBand,
          source: primaryFactor.source || null,
        }
      : null,
    contextFactors: contextFactors.map((f) => ({
      id: f.id,
      detail: f.detail,
      weight: f.weight,
    })),
    escalation,
    problemCount,
    hasCriticalOrHigh,
  }
}

export function compareRemediationPriority(a, b) {
  const bandA = upper(a?.remediationPriority?.band ?? a?.band)
  const bandB = upper(b?.remediationPriority?.band ?? b?.band)
  if (bandRank(bandB) !== bandRank(bandA)) return bandRank(bandB) - bandRank(bandA)

  const sa = a?.remediationPriority?.score ?? a?.score ?? 0
  const sb = b?.remediationPriority?.score ?? b?.score ?? 0
  if (sb !== sa) return sb - sa

  const ca = upper(a?.criticality || a?.row?.criticality)
  const cb = upper(b?.criticality || b?.row?.criticality)
  const cw = { CRITICAL: 4, HIGH: 3, MEDIUM: 2, LOW: 1 }
  if ((cw[cb] || 0) !== (cw[ca] || 0)) return (cw[cb] || 0) - (cw[ca] || 0)

  const na = String(a?.name || a?.row?.name || '')
  const nb = String(b?.name || b?.row?.name || '')
  return na.localeCompare(nb)
}

/**
 * Same formula as Home Total Security Health / scopedAggregateService:
 * percent = 100 - min(100, CRITICAL*4 + HIGH*3 + MEDIUM*2 + LOW*1); INFO excluded.
 * Must NOT use controlCoveragePercent (that is readiness coverage, not TSH).
 */
export function computeTshPercentFromSeverity(sev = {}) {
  const c = n(sev.critical ?? sev.CRITICAL)
  const h = n(sev.high ?? sev.HIGH)
  const m = n(sev.medium ?? sev.MEDIUM)
  const l = n(sev.low ?? sev.LOW)
  const riskScore = c * 4 + h * 3 + m * 2 + l * 1
  return Math.max(0, 100 - Math.min(100, riskScore))
}

/** Same thresholds as Home security-health API statusBySeverity. */
export function deriveSecurityHealthStatus(percent) {
  if (percent == null || !Number.isFinite(Number(percent))) return null
  const p = Number(percent)
  if (p >= 85) return 'Good'
  if (p >= 60) return 'Moderate'
  return 'Need Attention'
}

function severityFromTops(row = {}) {
  let c = 0
  let h = 0
  let m = 0
  let l = 0
  let info = 0
  for (const v of row.topVulnerabilities || []) {
    const s = upper(v.severity)
    if (s === 'CRITICAL') c += 1
    else if (s === 'HIGH') h += 1
    else if (s === 'MEDIUM') m += 1
    else if (s === 'LOW') l += 1
    else info += 1
  }
  return { critical: c, high: h, medium: m, low: l, info }
}

export function deriveSystemSecurityHealthPercent(row = {}) {
  const dist = row.severityDistribution || row.severity || null
  if (dist && typeof dist === 'object') {
    return computeTshPercentFromSeverity(dist)
  }
  return computeTshPercentFromSeverity(severityFromTops(row))
}

export function attachRemediationPriority(rows = [], executiveById = {}) {
  return (rows || []).map((row) => {
    const ex = executiveById[row.informationSystemId] || null
    const remediationPriority = computeRemediationPriority(row, ex)
    const securityHealthPercent = deriveSystemSecurityHealthPercent(row)
    return {
      ...row,
      remediationPriority,
      securityHealthPercent,
      securityHealthStatus: deriveSecurityHealthStatus(securityHealthPercent),
      controlCoveragePercent:
        row.readiness?.controlCoveragePercent != null
          ? Math.max(0, Math.min(100, Math.round(Number(row.readiness.controlCoveragePercent))))
          : null,
    }
  })
}
